import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { trackEvent } from '../utils/facebookPixel';

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const TeamInfo = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const TeamName = styled.h3`
  color: #333;
  margin: 0 0 0.5rem 0;
`;

const TeamDetails = styled.p`
  color: #666;
  margin: 0;
  font-size: 0.9rem;
`;

const PricingInfo = styled.div`
  background: #e3f2fd;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #1976d2;
  margin-bottom: 0.5rem;
`;

const PriceDescription = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background-color: #043663;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.7 : 1};
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: ${props => props.disabled ? '#ccc' : '#032a4a'};
  }
`;

const Status = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  background: ${props => props.$success ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$success ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.$success ? '#c3e6cb' : '#f5c6cb'};
`;

// eslint-disable-next-line no-unused-vars
const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const PaymentPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [teamInfo, setTeamInfo] = useState(null);
  const [registrationId, setRegistrationId] = useState(null);
  const [abandonmentTracked, setAbandonmentTracked] = useState(false);

  const invalidMsg = t('paymentFlows.paymentPage.invalidPaymentLink');

  // Get team info from URL params
  useEffect(() => {
    const teamId = searchParams.get('team_id');
    const regId = searchParams.get('registration_id');
    
    if (teamId && regId) {
      setRegistrationId(regId);
      // Fetch team details from API
      fetchTeamDetails(teamId);
      
      // Track payment page view
      trackEvent('ViewContent', {
        content_name: 'Payment Page',
        content_category: 'Payment',
        custom_data: {
          team_id: teamId,
          registration_stage: 'payment_page_viewed'
        }
      });

      // Track abandoned cart if user leaves page without completing payment
      const handleBeforeUnload = () => {
        trackEvent('AddToCart', {
          content_name: 'Team Registration - Abandoned',
          content_category: 'Registration',
          value: 160,
          currency: 'CHF',
          custom_data: {
            team_id: teamId,
            registration_stage: 'page_left',
            abandonment_type: 'browser_closed'
          }
        });
      };

      // Add event listener for page unload
      window.addEventListener('beforeunload', handleBeforeUnload);

      // Track abandonment after 30 seconds if user hasn't completed payment
      const abandonmentTimeout = setTimeout(() => {
        if (!abandonmentTracked) {
          trackEvent('AddToCart', {
            content_name: 'Team Registration - Abandoned',
            content_category: 'Registration',
            value: 160,
            currency: 'CHF',
            custom_data: {
              team_id: teamId,
              registration_stage: 'timeout_abandoned',
              abandonment_type: 'timeout_30_seconds'
            }
          });
          setAbandonmentTracked(true);
          console.log('Abandonment tracked after 30 seconds for team:', teamId);
        }
      }, 30000);

      // Cleanup event listener and timeout
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        clearTimeout(abandonmentTimeout);
      };
    } else {
      setStatus(invalidMsg);
    }
  }, [searchParams, invalidMsg]);

  const fetchTeamDetails = async (teamId) => {
    try {
      const response = await api.get(`/teams/${teamId}`);
      if (response.data.success) {
        setTeamInfo({
          teamName: response.data.data.team_name,
          canton: response.data.data.canton
        });
      }
    } catch (error) {
      console.error('Error fetching team details:', error);
      // Fallback to basic info
      setTeamInfo({
        teamName: t('paymentFlows.paymentPage.teamFallbackName'),
        canton: 'ZH'
      });
    }
  };

  const handlePayment = async () => {
    if (!registrationId) {
      setStatus(t('paymentFlows.paymentPage.registrationIdMissing'));
      return;
    }

    setLoading(true);
    setStatus('');

    try {
      console.log('Creating checkout session with:', {
        registration_id: registrationId,
        success_url: `${window.location.origin}/payment-success?team_id=${searchParams.get('team_id')}&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/payment-cancelled?team_id=${searchParams.get('team_id')}`
      });

      // Create Stripe Checkout Session
      const response = await api.post('/payments/create-checkout-session', {
        registration_id: registrationId,
        team_id: searchParams.get('team_id'),
        success_url: `${window.location.origin}/payment-success?team_id=${searchParams.get('team_id')}&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/payment-cancelled?team_id=${searchParams.get('team_id')}`
      });

      console.log('Full API Response:', response);
      console.log('Response data:', response.data);
      console.log('Response status:', response.status);

      if (response.data && response.data.success) {
        console.log('Redirecting to:', response.data.data.checkout_url);
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.checkout_url;
      } else if (response.data && response.data.checkout_url) {
        // Fallback: if response doesn't have success wrapper
        console.log('Redirecting to (fallback):', response.data.checkout_url);
        window.location.href = response.data.checkout_url;
      } else {
        console.error('API returned error:', response.data);
        const detail = response.data?.message || t('paymentFlows.paymentPage.unknownError');
        setStatus(t('paymentFlows.paymentPage.paymentSetupFailed', { detail }));
        setLoading(false);
      }

    } catch (error) {
      console.error('Payment error:', error);
      const detail = error.response?.data?.message || error.message;
      setStatus(t('paymentFlows.paymentPage.paymentFailed', { detail }));
      setLoading(false);
    }
  };

  const paymentSteps = t('paymentFlows.paymentPage.steps', { returnObjects: true });

  if (!registrationId) {
    return (
      <PageContainer>
        <Card>
          <Title>{t('paymentFlows.paymentPage.titlePaymentRequired')}</Title>
          <Status $success={false}>
            {status || invalidMsg}
          </Status>
          <Button onClick={() => navigate('/register')}>
            {t('paymentFlows.paymentPage.goToRegistration')}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card>
        <Title>{t('paymentFlows.paymentPage.titleCompleteRegistration')}</Title>

        {teamInfo && (
          <TeamInfo>
            <TeamName>{teamInfo.teamName}</TeamName>
            <TeamDetails>
              {t('paymentFlows.paymentPage.cantonLabel')} {teamInfo.canton}
            </TeamDetails>
          </TeamInfo>
        )}

        <PricingInfo>
          <Price>{t('paymentFlows.paymentPage.priceLine')}</Price>
          <PriceDescription>{t('paymentFlows.paymentPage.priceDescription')}</PriceDescription>
        </PricingInfo>

        <Button onClick={handlePayment} disabled={loading}>
          {loading ? t('paymentFlows.paymentPage.processing') : t('paymentFlows.paymentPage.payButton')}
        </Button>

        {status && (
          <Status $success={false}>
            {status}
          </Status>
        )}

        <div style={{ marginTop: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '4px' }}>
          <h3>{t('paymentFlows.paymentPage.whatHappensNext')}</h3>
          <ol>
            {Array.isArray(paymentSteps) &&
              paymentSteps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
          </ol>

          <h3>{t('paymentFlows.paymentPage.testCardsTitle')}</h3>
          <ul>
            <li>
              <strong>{t('paymentFlows.paymentPage.testSuccess')}</strong> 4242 4242 4242 4242
            </li>
            <li>
              <strong>{t('paymentFlows.paymentPage.testDecline')}</strong> 4000 0000 0000 0002
            </li>
            <li>
              <strong>{t('paymentFlows.paymentPage.testExpiry')}</strong> 12/34
            </li>
            <li>
              <strong>{t('paymentFlows.paymentPage.testCvc')}</strong> 123
            </li>
          </ul>
        </div>
      </Card>
    </PageContainer>
  );
};

export default PaymentPage;
